export const ORG_MANAGEMENT_PERM = "organization_management_system";
export const ERC_SUPERVISOR_PERM = "odjfs_erc_lic_super";
export const ERC_COUNTY_SUPERVISOR_PERM = "odjfs_erc_county_lic_super";

/**
 * The only groups an ERC supervisor may grant or revoke on their own org.
 * Mirrors PermissionModel::ERC_SUPERVISOR_ASSIGNABLE_GROUPS on the API side --
 * the API enforces this, the UI just avoids offering what would be ignored.
 */
export const ERC_SUPERVISOR_ASSIGNABLE_GROUPS = [
  "odjfs_erc_lic_spec",
  "odjfs_erc_lic_super",
  "odjfs_erc_county_lic_spec",
  "odjfs_erc_county_lic_super",
];

/**
 * Who may view and edit the user permissions of an organization. Mirrors
 * PermissionModel::canUserManageOrgUsers() on the API side.
 *
 * @param {string[]} orgPermissions - org_permissions for the selected org
 */
export const canManageOrgUsers = (orgPermissions) => {
  if (!Array.isArray(orgPermissions)) {
    return false;
  }
  return (
    orgPermissions.includes(ORG_MANAGEMENT_PERM) ||
    orgPermissions.includes(ERC_SUPERVISOR_PERM) ||
    orgPermissions.includes(ERC_COUNTY_SUPERVISOR_PERM)
  );
};

/**
 * ERC supervisors -- statewide or county -- administer the user permissions of their
 * own organization. Without org management they may only grant or revoke the ERC
 * roles (ERC_SUPERVISOR_ASSIGNABLE_GROUPS). This says nothing about their access to
 * the rest of the training org -- see hasTrainingOrgAccess() for that.
 *
 * @param {string[]} orgPermissions - org_permissions for the selected org
 */
export const isErcSupervisorOnly = (orgPermissions) => {
  if (!Array.isArray(orgPermissions)) {
    return false;
  }
  return (
    (orgPermissions.includes(ERC_SUPERVISOR_PERM) ||
      orgPermissions.includes(ERC_COUNTY_SUPERVISOR_PERM)) &&
    !orgPermissions.includes(ORG_MANAGEMENT_PERM)
  );
};

/**
 * What the API says the user may do on the selected org, from
 * /api/v2/organization/{id}/user/me. Null until that loads, and from an API that
 * predates capabilities; callers then fall back to the session flags.
 *
 * @param {object|null} orgMe - permissionsByTrainingOrg from useEventManagementStore
 */
const orgCapabilities = (orgMe) => orgMe?.capabilities ?? null;

/**
 * Whether the user gets the full training org menu (events, instructors, sharing...).
 * ERC supervisors without it only get the User Permissions page.
 *
 * @param {object} user - the session user from useAppStore
 * @param {object|null} orgMe - permissionsByTrainingOrg from useEventManagementStore
 */
export const hasTrainingOrgAccess = (user, orgMe = null) => {
  if (user?.is_admin) {
    return true;
  }
  const capabilities = orgCapabilities(orgMe);
  // pdtrack_data_entry spans all of the user's orgs, so it is only the fallback
  return capabilities
    ? !!capabilities.can_edit_trainings
    : !!user?.permissions?.pdtrack_data_entry;
};

/**
 * Whether the user may view and edit the user permissions of the selected org.
 *
 * @param {object} user - the session user from useAppStore
 * @param {object|null} orgMe - permissionsByTrainingOrg from useEventManagementStore
 */
export const canManageTrainingOrgUsers = (user, orgMe = null) => {
  if (user?.is_admin) {
    return true;
  }
  const capabilities = orgCapabilities(orgMe);
  return capabilities
    ? !!capabilities.can_manage_users
    : canManageOrgUsers(orgMe?.org_permissions) ||
        !!user?.permissions?.odjfs_erc_lic_super;
};

/**
 * The groups the user may grant or revoke on the selected org: null for any,
 * otherwise only the ERC roles, for user managers without org management.
 * Mirrors Organization::filterAssignableGroups() on the API side.
 *
 * @param {object} user - the session user from useAppStore
 * @param {object|null} orgMe - permissionsByTrainingOrg from useEventManagementStore
 */
export const getAssignableAliases = (user, orgMe = null) => {
  if (user?.is_admin) {
    return null;
  }
  const capabilities = orgCapabilities(orgMe);
  const orgPermissions = orgMe?.org_permissions;
  let ercRolesOnly;
  if (capabilities) {
    ercRolesOnly = !capabilities.can_manage_org;
  } else if (orgPermissions?.length) {
    ercRolesOnly = isErcSupervisorOnly(orgPermissions);
  } else {
    ercRolesOnly = !!user?.permissions?.odjfs_erc_lic_super;
  }
  return ercRolesOnly ? ERC_SUPERVISOR_ASSIGNABLE_GROUPS : null;
};
